"use client";

import { useState, useCallback } from 'react';
import { waxClientService, WaxUserAccount, WaxLoginResult } from '@/lib/wax';
import { clientLogger } from '@/lib/client-logger';

/**
 * Authentication states for wallet connection flows.
 */
export type WalletAuthState =
  | 'idle'
  | 'checking'
  | 'selecting'
  | 'authenticating'
  | 'connecting'
  | 'success'
  | 'error';

/**
 * Wallet type identifiers for UI display.
 */
export type WalletType = 'wax-cloud' | 'anchor' | 'wombat' | 'cloudwallet';

/**
 * Converts UI wallet type to internal wallet type for WaxUserAccount.
 */
function toInternalWalletType(walletType: WalletType): 'cloudwallet' | 'anchor' | 'wombat' {
  if (walletType === 'wax-cloud') return 'cloudwallet';
  return walletType as 'cloudwallet' | 'anchor' | 'wombat';
}

/**
 * Wallet option configuration.
 */
export interface WalletOption {
  id: WalletType;
  name: string;
  description: string;
  icon: string;
  color: string;
}

/**
 * Standard wallet options available for connection.
 */
export const WALLET_OPTIONS: WalletOption[] = [
  {
    id: 'wax-cloud',
    name: 'WAX Cloud Wallet',
    description: 'Easy web-based wallet with email login',
    icon: '☁️',
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100'
  },
  {
    id: 'anchor',
    name: 'Anchor Wallet',
    description: 'Desktop and mobile wallet with advanced security',
    icon: '⚓',
    color: 'bg-green-50 border-green-200 hover:bg-green-100'
  },
  {
    id: 'wombat',
    name: 'Wombat Wallet',
    description: 'Gaming-focused wallet with NFT support',
    icon: '🐨',
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100'
  }
];

/**
 * Return type for useWalletAuth hook.
 */
export interface UseWalletAuthReturn {
  /** Current authentication state */
  authState: WalletAuthState;
  /** Successfully authenticated account (null if not authenticated) */
  authenticatedAccount: WaxUserAccount | null;
  /** Error message if authentication failed */
  errorMessage: string;
  /** Currently selected wallet type */
  selectedWallet: WalletType | null;
  /** Whether authentication is in progress */
  isAuthenticating: boolean;
  /** Whether we're on testnet */
  isTestnet: boolean;
  /** Current network (mainnet or testnet) */
  network: 'mainnet' | 'testnet';
  /** Authenticate with WAX Cloud Wallet (not available on testnet) */
  authenticateWithCloudWallet: () => Promise<void>;
  /** Authenticate with a specific wallet type */
  authenticateWithWallet: (walletType: WalletType) => Promise<void>;
  /** Open wallet selection modal with all available wallets (respects network) */
  authenticateWithAnyWallet: () => Promise<void>;
  /** Check for existing auto-login session */
  checkAutoLogin: () => Promise<boolean>;
  /** Reset to initial state */
  reset: () => void;
  /** Retry after error */
  retry: () => void;
  /** Set custom error state */
  setError: (message: string) => void;
  /** Get wallet display info */
  getWalletInfo: (walletType: WalletType) => WalletOption | undefined;
  /** Get available wallet options based on network */
  getAvailableWallets: () => WalletOption[];
}

/**
 * Options for useWalletAuth hook.
 */
export interface UseWalletAuthOptions {
  /** Callback when authentication succeeds */
  onSuccess?: (account: WaxUserAccount) => void;
  /** Callback when authentication fails */
  onError?: (error: string) => void;
  /** Callback when loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Whether to validate account exists on blockchain */
  validateAccount?: boolean;
  /** Initial state */
  initialState?: WalletAuthState;
}

/**
 * Parses error messages into user-friendly format.
 */
function parseAuthError(error: unknown, walletType?: WalletType): string {
  if (!(error instanceof Error)) {
    return 'Authentication failed';
  }

  const message = error.message;

  if (message.includes('User rejected') || message.includes('cancelled')) {
    return 'Authentication was cancelled. Please try again.';
  }
  if (message.includes('popup')) {
    return 'Please allow popups and try again.';
  }
  if (message.includes('network')) {
    return 'Network error. Please check your connection and try again.';
  }
  if (message.includes('not installed')) {
    const walletName = walletType
      ? WALLET_OPTIONS.find(w => w.id === walletType)?.name
      : 'The wallet';
    return `${walletName} is not installed. Please install the wallet and try again.`;
  }

  return message;
}

/**
 * Hook for managing WAX wallet authentication state and flows.
 *
 * Consolidates the common authentication patterns used across:
 * - WaxAuthenticator
 * - MultiWalletAuthenticator
 * - ExistingWalletLinker
 *
 * @param options - Configuration options
 * @returns Wallet auth state and control functions
 *
 * @example
 * ```tsx
 * function WalletConnect() {
 *   const {
 *     authState,
 *     authenticatedAccount,
 *     errorMessage,
 *     authenticateWithCloudWallet,
 *     reset,
 *   } = useWalletAuth({
 *     onSuccess: (account) => console.log('Connected:', account.accountName),
 *     onError: (error) => console.error('Failed:', error),
 *   });
 *
 *   if (authState === 'success') {
 *     return <WalletSuccessDisplay account={authenticatedAccount} />;
 *   }
 *
 *   return (
 *     <Button onClick={authenticateWithCloudWallet}>
 *       Connect Wallet
 *     </Button>
 *   );
 * }
 * ```
 */
export function useWalletAuth(options: UseWalletAuthOptions = {}): UseWalletAuthReturn {
  const {
    onSuccess,
    onError,
    onLoadingChange,
    validateAccount = true,
    initialState = 'idle',
  } = options;

  const [authState, setAuthState] = useState<WalletAuthState>(initialState);
  const [authenticatedAccount, setAuthenticatedAccount] = useState<WaxUserAccount | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedWallet, setSelectedWallet] = useState<WalletType | null>(null);

  const isAuthenticating = authState === 'authenticating' || authState === 'connecting' || authState === 'checking';

  const setLoadingState = useCallback((state: WalletAuthState) => {
    setAuthState(state);
    const isLoading = state === 'authenticating' || state === 'connecting' || state === 'checking';
    onLoadingChange?.(isLoading);
  }, [onLoadingChange]);

  const handleSuccess = useCallback((account: WaxUserAccount, walletType?: WalletType) => {
    setAuthenticatedAccount(account);
    setSelectedWallet(walletType || null);
    setAuthState('success');
    setErrorMessage('');
    onLoadingChange?.(false);
    onSuccess?.(account);
  }, [onSuccess, onLoadingChange]);

  const handleError = useCallback((error: string) => {
    setErrorMessage(error);
    setAuthState('error');
    onLoadingChange?.(false);
    onError?.(error);
  }, [onError, onLoadingChange]);

  const checkAutoLogin = useCallback(async (): Promise<boolean> => {
    setLoadingState('checking');
    try {
      await waxClientService.initialize();
      const isAutoLoginAvailable = await waxClientService.isAutoLoginAvailable();

      if (isAutoLoginAvailable) {
        const currentAccount = waxClientService.getCurrentAccount();
        if (currentAccount) {
          handleSuccess(currentAccount, 'wax-cloud');
          return true;
        }
      }

      setAuthState('idle');
      return false;
    } catch (error) {
      clientLogger.error('Auto login check failed', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      setAuthState('idle');
      return false;
    }
  }, [setLoadingState, handleSuccess]);

  const validateAccountOnChain = useCallback(async (accountName: string): Promise<boolean> => {
    if (!validateAccount) return true;

    try {
      const accountAvailable = await waxClientService.isAccountAvailable(accountName);
      // Account is "available" means it doesn't exist - we want it to exist
      return !accountAvailable;
    } catch (validationError) {
      clientLogger.warn('Account validation failed, proceeding with authentication', {
        error: validationError instanceof Error ? validationError.message : 'Unknown error',
        accountName
      });
      // If validation fails due to network issues, proceed but log
      return true;
    }
  }, [validateAccount]);

  const processLoginResult = useCallback(async (
    result: WaxLoginResult,
    walletType: WalletType
  ): Promise<void> => {
    if (result.success && result.userAccount) {
      const accountName = result.userAccount.accountName;

      // Validate account exists on blockchain
      const exists = await validateAccountOnChain(accountName);
      if (!exists) {
        const error = `WAX account "${accountName}" not found on blockchain. Please ensure your account is valid.`;
        handleError(error);
        return;
      }

      // Build avatar URL based on wallet type
      const avatar = result.userAccount.avatar || (
        walletType === 'wax-cloud' || walletType === 'cloudwallet'
          ? `https://www.mycloudwallet.com/api/avatar/${accountName}`
          : `https://ui-avatars.com/api/?name=${accountName}&background=10b981&color=fff`
      );

      const account: WaxUserAccount = {
        accountName,
        pubKeys: result.userAccount.pubKeys || [],
        avatar,
        walletType: result.userAccount.walletType || toInternalWalletType(walletType),
      };

      handleSuccess(account, walletType);
    } else {
      handleError(result.error || 'Authentication failed');
    }
  }, [validateAccountOnChain, handleSuccess, handleError]);

  const authenticateWithCloudWallet = useCallback(async (): Promise<void> => {
    setSelectedWallet('wax-cloud');
    setLoadingState('authenticating');
    setErrorMessage('');

    try {
      await waxClientService.initialize();
      const result = await waxClientService.loginWithCloudWallet();
      await processLoginResult(result, 'wax-cloud');
    } catch (error) {
      const errorMsg = parseAuthError(error, 'wax-cloud');
      handleError(errorMsg);
    }
  }, [setLoadingState, processLoginResult, handleError]);

  const authenticateWithWallet = useCallback(async (walletType: WalletType): Promise<void> => {
    setSelectedWallet(walletType);
    setLoadingState('authenticating');
    setErrorMessage('');

    try {
      await waxClientService.initialize();

      let result: WaxLoginResult;

      switch (walletType) {
        case 'wax-cloud':
        case 'cloudwallet':
          result = await waxClientService.loginWithWallet('cloudwallet');
          break;
        case 'anchor':
          result = await waxClientService.loginWithWallet('anchor');
          break;
        case 'wombat':
          result = await waxClientService.loginWithWallet('wombat');
          break;
        default:
          throw new Error('Unsupported wallet type');
      }

      await processLoginResult(result, walletType);
    } catch (error) {
      const errorMsg = parseAuthError(error, walletType);
      handleError(errorMsg);
    }
  }, [setLoadingState, processLoginResult, handleError]);

  const authenticateWithAnyWallet = useCallback(async (): Promise<void> => {
    setLoadingState('authenticating');
    setErrorMessage('');

    try {
      await waxClientService.initialize();

      // login() automatically excludes Cloud Wallet on testnet
      const result = await waxClientService.login();

      if (result.success && result.userAccount) {
        const walletType = result.userAccount.walletType === 'cloudwallet' ? 'wax-cloud' : result.userAccount.walletType;
        await processLoginResult(result, walletType as WalletType);
      } else {
        handleError(result.error || 'Authentication failed');
      }
    } catch (error) {
      const errorMsg = parseAuthError(error);
      handleError(errorMsg);
    }
  }, [setLoadingState, processLoginResult, handleError]);

  const reset = useCallback(() => {
    setAuthState('idle');
    setErrorMessage('');
    setAuthenticatedAccount(null);
    setSelectedWallet(null);
    onLoadingChange?.(false);
  }, [onLoadingChange]);

  const retry = useCallback(() => {
    setAuthState('idle');
    setErrorMessage('');
  }, []);

  const setError = useCallback((message: string) => {
    handleError(message);
  }, [handleError]);

  const getWalletInfo = useCallback((walletType: WalletType): WalletOption | undefined => {
    return WALLET_OPTIONS.find(w => w.id === walletType);
  }, []);

  const getAvailableWallets = useCallback((): WalletOption[] => {
    const isTestnetMode = waxClientService.isTestnet();
    if (isTestnetMode) {
      // On testnet, Cloud Wallet is not available
      return WALLET_OPTIONS.filter(w => w.id !== 'wax-cloud');
    }
    return WALLET_OPTIONS;
  }, []);

  // Get network info (these will use the initialized values after first call)
  const isTestnet = waxClientService.isTestnet();
  const network = waxClientService.getNetwork();

  return {
    authState,
    authenticatedAccount,
    errorMessage,
    selectedWallet,
    isAuthenticating,
    isTestnet,
    network,
    authenticateWithCloudWallet,
    authenticateWithWallet,
    authenticateWithAnyWallet,
    checkAutoLogin,
    reset,
    retry,
    setError,
    getWalletInfo,
    getAvailableWallets,
  };
}

export default useWalletAuth;
